import { useState, useCallback, useMemo } from 'react';
import { Modal, Button, useLocale, type Option, Tooltip } from 'shared-ui';
import { Paper } from '@mui/material';

import { AppMessages } from 'i18n/messages';
import { ReportErrorAlert } from '../reportErrorAlert';

import * as Styled from './EmailSelectionModal.styles';
import { EmailSelectionModalProps } from './EmailSelectionModal.types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailSelectionModal = ({
  open,
  onClose,
  onConfirm,
  availableUsers,
  isLoading = false,
  selectedUsers: initialUsers = [],
  title,
  isSmtpConfigured = true,
  hasNoDataError = false,
  onResetError,
}: EmailSelectionModalProps) => {
  const { formatMessage } = useLocale();
  const [selectedUsers, setSelectedUsers] = useState<Option[]>(initialUsers);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validateEmail = useCallback((email: string | number): boolean => {
    return EMAIL_REGEX.test(email.toString().trim());
  }, []);

  const userOptions = useMemo(() => availableUsers, [availableUsers]);

  const handleChange = useCallback(
    (_: unknown, newValue: unknown[]) => {
      setError(null);
      onResetError?.();

      // Convert to Option format and validate all emails
      const convertedValue: Option[] = newValue.map((item) => {
        if (typeof item === 'string') {
          return { value: item, label: item };
        }
        return item as Option;
      });

      const invalidEmails = convertedValue.filter((email) => !validateEmail(email.value));
      if (invalidEmails.length > 0) {
        setError(formatMessage({ id: AppMessages['reports.recipients.validation.email'] }));
        return;
      }

      setSelectedUsers(convertedValue);
    },
    [validateEmail, formatMessage, onResetError],
  );

  const handleInputChange = useCallback(
    (_: unknown, newInputValue: string) => {
      setInputValue(newInputValue);
      setError(null);
      onResetError?.();
    },
    [onResetError],
  );

  const handleConfirm = () => {
    if (selectedUsers.length === 0) {
      return setError(formatMessage({ id: AppMessages['common.validation.required'] }));
    }

    const invalidEmails = selectedUsers.filter((email) => !validateEmail(email.value));
    if (invalidEmails.length > 0) {
      return setError(formatMessage({ id: AppMessages['reports.recipients.validation.email'] }));
    }

    onConfirm(selectedUsers.map((email) => email.value as string));
  };

  const handleClose = useCallback(() => {
    setInputValue('');
    setError(null);
    onResetError?.();
    onClose();
  }, [onClose, onResetError]);

  return (
    <>
      <Modal
        type="default"
        open={open}
        onClose={handleClose}
        header={title || formatMessage({ id: AppMessages['reports.selectRecipients'] })}
        size="medium"
        withBackButton
        onClickBackButton={onClose}
      >
        <Styled.Container>
          <div>
            <Styled.Label>
              {formatMessage({ id: AppMessages['reports.recipients.label'] })}
              <Styled.Required> *</Styled.Required>
            </Styled.Label>

            <Styled.Autocomplete
              multiple
              freeSolo
              openOnFocus
              forcePopupIcon
              disableCloseOnSelect
              options={userOptions || []}
              value={selectedUsers}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              loading={isLoading}
              loadingText={formatMessage({ id: AppMessages['common.loading'] })}
              clearIcon={<Styled.RemoveIcon />}
              popupIcon={<Styled.DropdownArrowIcon />}
              onChange={handleChange}
              PaperComponent={(props) => <Paper {...props} style={{ border: 'none' }} />}
              ListboxComponent={(props) => <Paper {...props} sx={{ border: '1px solid', borderColor: 'grey.500' }} />}
              renderOption={(props, option) => (
                <li {...props}>
                  <Styled.OptionNumber>{(option as Option)?.label}</Styled.OptionNumber>
                  <Styled.OptionLabel>{(option as Option)?.value}</Styled.OptionLabel>
                </li>
              )}
              renderInput={(params) => (
                <Styled.StyledTextField
                  {...params}
                  variant="filled"
                  fullWidth
                  placeholder={
                    selectedUsers.length === 0
                      ? formatMessage({ id: AppMessages['reports.recipients.placeholder'] })
                      : ''
                  }
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : (option as Option).value;
                  return (
                    <Styled.StyledChip
                      variant="filled"
                      label={optionValue}
                      deleteIcon={<Styled.RemoveIcon />}
                      {...getTagProps({ index })}
                      key={optionValue}
                    />
                  );
                })
              }
              className="autocomplete-container"
            />

            {error && <Styled.ErrorText>{error}</Styled.ErrorText>}

            {hasNoDataError && (
              <Styled.ErrorWrapper>
                <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
              </Styled.ErrorWrapper>
            )}
          </div>
        </Styled.Container>

        <Styled.Footer>
          <Button variant="text" onClick={handleClose} size="small">
            {formatMessage({ id: AppMessages['common.cancel'] })}
          </Button>
          <Tooltip
            title={!isSmtpConfigured ? formatMessage({ id: AppMessages['reports.serverNotConfigured'] }) : ''}
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handleConfirm}
                size="small"
                disabled={isLoading || selectedUsers.length === 0 || !isSmtpConfigured}
              >
                {formatMessage({ id: AppMessages['common.send'] })}
              </Button>
            </span>
          </Tooltip>
        </Styled.Footer>
      </Modal>
    </>
  );
};
